import { useCallback, useEffect, useRef } from 'react';
import ContentManagementInterface from '../../../models/ContentManagementInterface';
import {
  findCurrentBlock,
  getScrollOffset,
  getScrollTargetId,
} from './newContentManagementEditorUtils';
import { PageMode } from './newContentManagementEditorTypes';

interface UseContentEditorScrollSyncOptions {
  pageMode: PageMode;
  content: ContentManagementInterface | null;
  toolbarRef: React.RefObject<HTMLDivElement | null>;
}

export function useContentEditorScrollSync({
  pageMode,
  content,
  toolbarRef,
}: UseContentEditorScrollSyncOptions): void {
  const latestContent = useRef<ContentManagementInterface | null>(content);
  const lastVisibleBlockId = useRef<string | null>(null);

  useEffect(() => {
    latestContent.current = content;
  }, [content]);

  const resolveScrollOffset = useCallback((): number => {
    const toolbarHeight = toolbarRef.current?.offsetHeight ?? 56;
    return getScrollOffset(toolbarHeight);
  }, [toolbarRef]);

  useEffect(() => {
    const blocks = latestContent.current?.content;
    if (!blocks?.length) return;

    const handleScroll = () => {
      const block = findCurrentBlock(blocks, pageMode, resolveScrollOffset());
      if (block) lastVisibleBlockId.current = block.id;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [pageMode, resolveScrollOffset]);

  useEffect(() => {
    if (!lastVisibleBlockId.current || !latestContent.current?.content) return;

    const block = latestContent.current.content.find(
      (entry) => entry.id === lastVisibleBlockId.current,
    );
    if (!block) return;

    const targetId = getScrollTargetId(block, pageMode);
    const timer = setTimeout(() => {
      const el = document.getElementById(targetId);
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - resolveScrollOffset();
        window.scrollTo({ top, behavior: 'smooth' });
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [pageMode, resolveScrollOffset]);
}
